import { useState } from 'react';
import { useTranslate, SimpleForm, ReferenceInput, AutocompleteInput, required } from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
} from '@mui/material';

export interface TransferTicketDialogProps {
  open: boolean;
  onClose: () => void;
  onTransfer: (data: { toUserId?: number; toQueueId?: number }) => Promise<void>;
  currentUserId: number;
  isLoading?: boolean;
}

export function TransferTicketDialog({
  open,
  onClose,
  onTransfer,
  currentUserId,
  isLoading = false,
}: TransferTicketDialogProps) {
  const translate = useTranslate();
  const [type, setType] = useState<'user' | 'queue'>('user');

  const handleSubmit = async (data: { userId?: number; queueId?: number }) => {
    const transferData = type === 'user' && data.userId
      ? { toUserId: data.userId }
      : type === 'queue' && data.queueId
        ? { toQueueId: data.queueId }
        : null;

    if (transferData) {
      await onTransfer(transferData);
      handleClose();
    }
  };

  const handleClose = () => {
    setType('user');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {translate('resources.ticket.dialogs.transfer.title')}
      </DialogTitle>
      <SimpleForm onSubmit={handleSubmit} toolbar={false}>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
            <FormControl fullWidth size="small">
              <InputLabel>{translate('resources.ticket.dialogs.transfer.type')}</InputLabel>
              <Select
                value={type}
                onChange={(e) => setType(e.target.value as 'user' | 'queue')}
                label={translate('resources.ticket.dialogs.transfer.type')}
              >
                <MenuItem value="user">{translate('resources.ticket.dialogs.transfer.toUser')}</MenuItem>
                <MenuItem value="queue">{translate('resources.ticket.dialogs.transfer.toQueue')}</MenuItem>
              </Select>
            </FormControl>

            {type === 'user' && (
              <ReferenceInput
                source="userId"
                reference="user"
                filter={{ id_ne: currentUserId }}
              >
                <AutocompleteInput
                  label={translate('resources.ticket.dialogs.transfer.selectUser')}
                  optionText={(choice) => choice?.name || choice?.email || ''}
                  validate={required()}
                  fullWidth
                />
              </ReferenceInput>
            )}

            {type === 'queue' && (
              <ReferenceInput
                source="queueId"
                reference="ticket-queue"
              >
                <AutocompleteInput
                  label={translate('resources.ticket.dialogs.transfer.selectQueue')}
                  optionText="name"
                  validate={required()}
                  fullWidth
                />
              </ReferenceInput>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading}>
            {translate('ra.action.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : null}
          >
            {translate('resources.ticket.actions.transfer')}
          </Button>
        </DialogActions>
      </SimpleForm>
    </Dialog>
  );
}
